import { forwardRef, type ButtonHTMLAttributes, type CSSProperties, type MouseEvent } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Spinner } from './loader'
import { cn } from '@/lib/utils'
import { placed, placedChild, placedVariants } from '@/lib/style-guard'
import { contentClasses, type ContentProps } from '@/lib/content-props'

/**
 * Button — industry-standard styling.
 *
 * Rules (locked):
 * - Hover: background-color shift only, NO transform/translate (Linear,
 *   Stripe, Notion, GitHub all use pure color shift — translate-y feels
 *   playful and out of place in a B2B app).
 * - Active (pressed): slight bg-darker.
 * - Focus: shared focus-ring utility, never a custom ring per variant.
 * - Transition: `transition-colors` only (NOT `transition-all`).
 * - Heights are token-aligned (xs/sm/default/lg/xl/icon) — primitives
 *   like SegmentedControl and TabChips share these tokens so they line
 *   up in toolbars.
 */
const buttonLook = cva(
  [
    // Labels fit their container; token heights are floors for wrapped text.
    // Icon-only variants retain fixed dimensions and never lose their target.
    'label-fit inline-flex items-center justify-center gap-2 font-medium',
    'transition-colors duration-150 ease-out',
    'focus-visible:outline-none focus-ring',
    'disabled:pointer-events-none disabled:opacity-[var(--btn-disabled-opacity)]',
    'cursor-pointer',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: [
          'bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)]',
          'hover:bg-[var(--btn-primary-bg-hover)]',
          'active:bg-[var(--btn-primary-bg-active)]',
        ].join(' '),
        secondary: [
          'bg-[var(--btn-secondary-bg)] text-[var(--btn-secondary-text)]',
          'border border-[var(--btn-secondary-border)]',
          'hover:bg-[var(--btn-secondary-bg-hover)]',
        ].join(' '),
        outline: [
          'bg-transparent text-[var(--text-primary)]',
          'border border-[var(--border-default)]',
          'hover:bg-[var(--surface-muted)] hover:border-[var(--border-strong)]',
        ].join(' '),
        ghost: [
          'bg-[var(--btn-ghost-bg)] text-[var(--btn-ghost-text)]',
          'hover:bg-[var(--btn-ghost-bg-hover)]',
        ].join(' '),
        danger: [
          'bg-[var(--btn-danger-bg)] text-[var(--btn-danger-text)]',
          'hover:bg-[var(--btn-danger-bg-hover)]',
        ].join(' '),
        success: [
          'bg-[var(--color-success-500)] text-[var(--on-success)]',
          'hover:bg-[var(--color-success-700)]',
        ].join(' '),
        accent: [
          'bg-[var(--color-accent-500)] text-[var(--on-accent)]',
          'hover:bg-[var(--color-accent-600)]',
        ].join(' '),
        /* The quiet variants below were page classes until 30.10.2026: 30 call sites wrote the
           same ink and hover onto `ghost` by hand, each a little differently. One name each now. */
        /** A secondary action that stays out of the way until pointed at (help, feedback, theme). */
        quiet: [
          'bg-transparent text-[var(--text-secondary)]',
          'hover:bg-[var(--btn-ghost-bg-hover)] hover:text-[var(--text-primary)]',
        ].join(' '),
        /** Remove/delete as a text or icon button, red from the start. */
        ghostDanger: [
          'bg-transparent text-[var(--status-error-text)]',
          'hover:bg-[var(--status-error-bg)]',
        ].join(' '),
        /** Remove in a dense list: neutral at rest, red only when pointed at or focused. */
        quietDanger: [
          'bg-transparent text-[var(--text-secondary)]',
          'hover:bg-[var(--status-error-bg)] hover:text-[var(--status-error-text)] focus-visible:text-[var(--status-error-text)]',
        ].join(' '),
        /** An «add one more» slot at the end of a list. */
        dashed: [
          'bg-transparent text-[var(--text-secondary)]',
          'border border-dashed border-[var(--border-default)]',
          'hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)]',
        ].join(' '),
        /** A ghost action in the brand's interactive colour («+ Legg til alternativ»). */
        ghostBrand: [
          'bg-transparent text-[var(--icon-primary)]',
          'hover:bg-[var(--btn-ghost-bg-hover)]',
        ].join(' '),
        /** An outline button on a dark public bar (the attendee header). */
        onDark: [
          'bg-transparent text-[var(--text-public-primary)]',
          'border border-[var(--border-on-dark)]',
          'hover:bg-[var(--surface-on-dark-hover)]',
        ].join(' '),
        link: [
          // `.link-brand` carries the colour AND 30b·4's escape hatch (neutral
          // text + brand underline when the compliant link drifts too far).
          'bg-transparent link-brand',
          // Task 40: `link-brand` now underlines AT REST, which is right for a
          // link in running text and wrong for a button. A button in an action
          // row is known by its placement and its shape, so it opts OUT here
          // explicitly rather than inheriting the underline silently. Hover
          // still restores it, so the affordance is not lost — only the resting
          // decoration a button has no business carrying.
          'no-underline hover:underline',
          'h-auto p-0',
        ].join(' '),
        public: [
          'bg-[var(--color-public-primary)] text-[var(--color-public-primary-text)] font-semibold',
          'shadow-[var(--shadow-public-cta)]',
          'hover:bg-[var(--color-public-primary-hover)] hover:shadow-[var(--shadow-public-cta-lg)]',
          'rounded-full',
        ].join(' '),
      },
      size: {
        xs: 'py-1 min-h-[var(--btn-height-xs)] px-[var(--btn-px-xs)] text-[length:var(--btn-font-size-xs)] rounded-[var(--btn-radius-sm)] [&_svg]:size-3.5',
        /* `sm` IS 44px below lg, and that is husregel 7 read literally: a
           touch target is measured «i det minste vindfanget der den finnes»,
           and `--btn-height-sm` is 32. Measured at 375px across five routes
           before this — «Fordel rom», «Vis →», «Kopier», «Abonner på
           kalender», «Handlinger», «Innstillinger» — every one a 32px target
           on a phone. Desktop keeps the 32px density from `lg` up, where the
           24–43px allowance applies and a pointer is precise. One rule here
           instead of twenty call-site patches. */
        sm: 'py-1 min-h-11 lg:min-h-[var(--btn-height-sm)] px-[var(--btn-px-sm)] text-[length:var(--btn-font-size-sm)] rounded-[var(--btn-radius-sm)] [&_svg]:size-4',
        default: 'py-1 min-h-[var(--btn-height-base)] px-[var(--btn-px-base)] text-[length:var(--btn-font-size-base)] rounded-[var(--btn-radius)] [&_svg]:size-4',
        lg: 'py-1 min-h-[var(--btn-height-lg)] px-[var(--btn-px-lg)] text-[length:var(--btn-font-size-lg)] rounded-[var(--btn-radius)] [&_svg]:size-5',
        xl: 'py-1 min-h-[var(--btn-height-xl)] px-[var(--btn-px-xl)] text-[length:var(--btn-font-size-xl)] font-semibold rounded-[var(--btn-radius-lg)] [&_svg]:size-5',
        icon: 'shrink-0 size-11 rounded-[var(--btn-radius)] [&_svg]:size-5',
        /* The icon button that sits in a row of `sm` buttons (a ⋯ menu beside «Legg til»): 44px on
           a phone like `sm`, and `sm`'s 32px from lg, so one toolbar has one height (sweep
           24.10.2026: 32 / 32 / 44 on every event tab). */
        'icon-sm': 'shrink-0 size-11 lg:size-[var(--btn-height-sm)] rounded-[var(--btn-radius-sm)] [&_svg]:size-4',
        /** A full-width row in a rail or list (help, feedback, theme, «new group chat»): icon, then label. */
        row: 'min-h-11 w-full justify-start gap-3 px-3 py-1 text-[length:var(--btn-font-size-base)] rounded-[var(--btn-radius)] [&_svg]:size-5',
        /** A button that IS a tile: a choice with a title and a line of text, left-aligned, wrapping. */
        tile: 'h-auto min-h-11 w-full justify-start whitespace-normal p-3 text-left rounded-[var(--btn-radius)]',
      },
    },
    /**
     * A DESTRUCTIVE BUTTON IS 44px, WHATEVER SIZE IT ASKED FOR.
     *
     * Husregel 7 is unconditional about this — «Destruktiv → 44 px uansett» —
     * and it was being missed by construction: `--btn-height-sm` is 32px, so
     * every `variant="danger" size="sm"` in the app rendered a 32px target for
     * the one class of action you must not hit by accident. Measured on
     * /settings: «Slett konto», 32px.
     *
     * Enforced HERE rather than at the call sites, because a rule that each
     * author has to remember is a rule that holds until the next author. This
     * makes the floor structural: `danger` cannot be small, and nobody has to
     * know that.
     */
    compoundVariants: [
      { variant: 'danger', size: 'xs', class: 'min-h-[var(--btn-height-base)]' },
      { variant: 'danger', size: 'sm', class: 'min-h-[var(--btn-height-base)] lg:min-h-[var(--btn-height-base)]' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
)

/** The look, for an element that is not a Button (a link styled as one). Its `className` option is a caller's, checked like the prop. */
const buttonVariants = placedVariants(buttonLook, 'buttonVariants')

/** Colours that come from data at run time (a customer's brand, a vote's colour), painted by the Button. */
export interface ButtonPaint {
  background?: string
  text?: string
  /** `currentColor` follows `text`. */
  border?: string
}

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonLook>,
    Pick<ContentProps, 'wrap'> {
  asChild?: boolean
  loading?: boolean
  /** A drag handle: no touch scrolling, a grab cursor, grabbing while held. */
  drag?: boolean
  paint?: ButtonPaint
}

const DRAG = 'touch-none cursor-grab active:cursor-grabbing'
const paintStyle = (paint: ButtonPaint | undefined, style: CSSProperties | undefined): CSSProperties | undefined =>
  paint ? { backgroundColor: paint.background, color: paint.text, borderColor: paint.border, ...style } : style

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, wrap, drag = false, paint, children, disabled, ...props }, ref) => {
    const look = (extra?: string | false) => cn(buttonLook({ variant, size, className: cn(contentClasses({ wrap }), drag && DRAG, placed(className, 'Button', props)) }), extra)
    const style = paintStyle(paint, props.style)
    if (asChild) {
      // `disabled` is a <button> attribute — a Slot child (usually a <Link>)
      // silently ignores it, rendering a normal-looking link that navigates.
      // Express the disabled contract in ways an anchor understands.
      const isDisabled = disabled || loading
      // Slot merges the child's own classes into the button: they are received classes too.
      placedChild(children, 'Button')
      return (
        <Slot
          className={look(isDisabled && 'pointer-events-none opacity-50')}
          ref={ref}
          aria-disabled={isDisabled || undefined}
          tabIndex={isDisabled ? -1 : undefined}
          {...props}
          style={style}
          // `pointer-events-none` stops the mouse, not a link that already had focus when it
          // became disabled: Enter still fired its click (IS-09). Swallow activation here, so the
          // contract holds for keyboard too. react-router's <Link> skips navigation when the
          // event arrives default-prevented.
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            if (isDisabled) {
              event.preventDefault()
              event.stopPropagation()
              return
            }
            props.onClick?.(event)
          }}
        >
          {children}
        </Slot>
      )
    }

    return (
      <button
        className={look()}
        ref={ref}
        disabled={disabled || loading}
        {...props}
        style={style}
      >
        {loading && <Spinner size="sm" />}
        {children}
      </button>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
export type { ButtonProps }
